/**
 * VistoeraTASK-00634 / 00635 — session cache for current member effective auth.
 *
 * Source: GET /workspaces/{id}/auth-matrix/me (Phase 1 Resolver via build_member_matrix).
 * Keyed by `${userId}::${workspaceId}` — never reuse across account / workspace.
 *
 * States: idle | loading | ready | error
 * - idle / loading: hide create entries and unconfirmed channels (no flash).
 * - ready: filter by effective_allowed.
 * - error (TASK-00635): fail-CLOSED — never fall back to unfiltered catalog /
 *   provider lists. UI shows retry; backend intercept remains the safety net.
 */
import type { AuthMatrixRow, AuthMemberMatrixResponse } from '../../../types'

export type AuthMatrixStatus = 'idle' | 'loading' | 'ready' | 'error'

let _status: AuthMatrixStatus = 'idle'
let _fetchedForKey: string | null = null
let _revision = 0
let _requestSeq = 0
let _inflight: { key: string; promise: Promise<void>; token: number } | null = null
let _enforcementMode: string | null = null
/** Last workspace/user used for ensure — needed by retry without props threading. */
let _lastWorkspaceId: string | null = null
let _lastUserId: string | null = null
/** node_type → effective_allowed */
let _nodeAllowed = new Map<string, boolean>()
/** node_type → Set of allowed provider_option_id */
let _allowedChannelsByNode = new Map<string, Set<string>>()
const _listeners = new Set<() => void>()

function cacheKey(workspaceId: string, userId: string | null | undefined): string {
  return `${userId || ''}::${workspaceId}`
}

function _notify() {
  _revision += 1
  for (const l of _listeners) l()
}

function _clear() {
  _nodeAllowed = new Map()
  _allowedChannelsByNode = new Map()
  _enforcementMode = null
}

export function getAuthMatrixRevision(): number {
  return _revision
}

export function getAuthMatrixStatus(): AuthMatrixStatus {
  return _status
}

export function getAuthMatrixEnforcementMode(): string | null {
  return _enforcementMode
}

export function subscribeAuthMatrix(listener: () => void): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

export function applyMemberAuthMatrix(rows: AuthMatrixRow[], enforcementMode?: string | null): void {
  const nodes = new Map<string, boolean>()
  const channels = new Map<string, Set<string>>()
  for (const row of rows || []) {
    const nid = row.node?.subject_id || row.node?.node_type
    if (!nid) continue
    nodes.set(nid, row.node.effective_allowed !== false)
    const allowed = new Set<string>()
    for (const ch of row.channels || []) {
      const cid = ch.provider_option_id || ch.subject_id
      if (!cid) continue
      if (ch.effective_allowed !== false) allowed.add(cid)
    }
    channels.set(nid, allowed)
  }
  _nodeAllowed = nodes
  _allowedChannelsByNode = channels
  if (enforcementMode != null) _enforcementMode = enforcementMode
  _notify()
}

/**
 * Node create / palette visibility.
 * idle / loading / error → false (TASK-00635: ERROR must not unlock full catalog).
 * READY → effective_allowed only (TASK-00639: missing matrix row = denied).
 */
export function isNodeEffectiveAllowed(nodeType: string): boolean {
  if (_status !== 'ready') return false
  return _nodeAllowed.get(nodeType) === true
}

/** True when matrix is READY and the node is explicitly denied. */
export function isNodeAuthDenied(nodeType: string): boolean {
  if (_status !== 'ready') return false
  return _nodeAllowed.get(nodeType) === false
}

/**
 * Channel visibility for a specific node (node_type + provider_option_id).
 * Not READY (idle/loading/error) → false — never flash or fall back to
 * unfiltered provider lists (TASK-00635).
 */
export function isChannelEffectiveAllowed(nodeType: string, providerOptionId: string | null | undefined): boolean {
  if (_status !== 'ready') return false
  if (!providerOptionId) return false
  const set = _allowedChannelsByNode.get(nodeType)
  if (!set) {
    if (_nodeAllowed.get(nodeType) === false) return false
    return true
  }
  return set.has(providerOptionId)
}

export function filterProviderOptionsByAuth<T extends { provider_option_id?: string | null }>(
  nodeType: string,
  options: T[],
): T[] {
  if (_status !== 'ready') return []
  return options.filter((o) => isChannelEffectiveAllowed(nodeType, o.provider_option_id))
}

export function filterChannelsByAuth<T extends { provider_option_id?: string | null }>(
  nodeType: string,
  channels: T[],
): T[] {
  return filterProviderOptionsByAuth(nodeType, channels)
}

export function invalidateAuthMatrixCache(): void {
  _requestSeq += 1
  _inflight = null
  _fetchedForKey = null
  _status = 'idle'
  _lastWorkspaceId = null
  _lastUserId = null
  _clear()
  _notify()
}

export async function ensureWorkspaceAuthMatrixLoaded(
  workspaceId: string,
  userId?: string | null,
  fetchMatrix?: (workspaceId: string) => Promise<AuthMemberMatrixResponse>,
): Promise<void> {
  if (!workspaceId) return
  _lastWorkspaceId = workspaceId
  _lastUserId = userId ?? null
  const key = cacheKey(workspaceId, userId)
  if (_fetchedForKey === key && _status === 'ready') return
  if (_inflight && _inflight.key === key) return _inflight.promise

  if (_fetchedForKey !== key) {
    _fetchedForKey = null
    _status = 'loading'
    _clear()
    _notify()
  } else if (_status !== 'ready') {
    _status = 'loading'
    _notify()
  }

  const token = ++_requestSeq
  const promise = (async () => {
    try {
      const payload = fetchMatrix
        ? await fetchMatrix(workspaceId)
        : await (async () => {
            const { workspaceGetMyAuthMatrix } = await import('../../../api')
            const res = await workspaceGetMyAuthMatrix(workspaceId)
            return res.data
          })()
      if (token !== _requestSeq) return
      _fetchedForKey = key
      _status = 'ready'
      applyMemberAuthMatrix(payload.rows || [], payload.enforcement_mode)
    } catch {
      if (token !== _requestSeq) return
      _fetchedForKey = key
      _status = 'error'
      _clear()
      _notify()
    } finally {
      if (_inflight?.token === token) _inflight = null
    }
  })()
  _inflight = { key, promise, token }
  return promise
}

/** User-driven retry after ERROR (TASK-00635). */
export function retryWorkspaceAuthMatrix(
  workspaceId?: string | null,
  userId?: string | null,
): Promise<void> {
  const ws = workspaceId || _lastWorkspaceId
  if (!ws) return Promise.resolve()
  _fetchedForKey = null
  _status = 'loading'
  _clear()
  _notify()
  return ensureWorkspaceAuthMatrixLoaded(ws, userId ?? _lastUserId)
}

export function resetAuthMatrixCacheForTests(): void {
  invalidateAuthMatrixCache()
}
